import type { ReactElement } from "react";
import type { Angebot } from "@kundeninfo/api-client";
import { Auswahlmenue } from "@kundeninfo/ui/me-design";
import type { OfferSeriesRef } from "./ReplacesChips.js";
import { SUCCESSION_TEXT as T } from "./texts.js";

/** Ein Kandidat des Feldes: Kennung der jüngsten Version, Nummer und Titel. */
export type ReplacesCandidate = Pick<Angebot, "id" | "displayNumber" | "title">;

/**
 * Die Kandidaten des Feldes „Ersetzt“ aus der Angebotsliste des Kunden: die jüngste Version jeder ÜBRIGEN Reihe,
 * historische eingeschlossen — nie die eigene Reihe (ein Angebot ersetzt sich nicht selbst, 422 ERSATZ_SELBST). Der
 * gespeicherte Verweis (`stored`) steht immer darunter: Die Liste lädt erst mit offenem Panel, und bis sie da ist (oder
 * wenn die Reihe darin fehlt) zeigte die Auswahl sonst einen Wert ohne Option (Task 27).
 */
export function replacesCandidates(offers: readonly Angebot[], ownSeriesId: string,
  stored: OfferSeriesRef | null = null): ReplacesCandidate[] {
  const candidates: ReplacesCandidate[] = offers.filter((a) => a.istJuengste && a.seriesId !== ownSeriesId);
  if (stored !== null && !candidates.some((c) => c.id === stored.offerId)) {
    candidates.push({ id: stored.offerId, displayNumber: stored.displayNumber, title: stored.title });
  }
  return candidates;
}

/**
 * Das Feld „Ersetzt“ im Kopfdaten-Abschnitt des Bearbeiten-Panels (Spec „Kopieren und Nachfolge“): eine Auswahl mit
 * Suche (`me-auswahlmenue`), leerbar über „— keines —“. Es schreibt NICHT selbst — „Speichern“ ist die Bestätigung
 * (Dirk 216c) und ruft `setOfferReplaces`, wenn sich der Wert geändert hat. `wert` ist die Kennung der jüngsten Version
 * der ersetzten Reihe (`replaces.offerId`), `null` heißt „ersetzt keine“.
 */
export function ReplacesField(p: {
  kandidaten: readonly ReplacesCandidate[];
  wert: string | null;
  onChange: (wert: string | null) => void;
  /** Feld-Kennung (Sprung zum ersten Fehler) und ein Befund des Servers am Feld (422 ERSATZ_RING …). */
  id?: string | undefined;
  error?: string | undefined;
}): ReactElement {
  return (
    <Auswahlmenue {...(p.id === undefined ? {} : { id: p.id, name: p.id })} beschriftung={T.field}
      wert={p.wert ?? ""} suchtext={T.fieldSearch} sucheAb={1} platzhalter={T.fieldNone}
      {...(p.error === undefined ? {} : { fehler: p.error })}
      onGeaendert={(d) => p.onChange(d.wert === "" ? null : d.wert)}>
      <option value="" data-art="niemand">{T.fieldNone}</option>
      {p.kandidaten.map((a) => (
        <option key={a.id} value={a.id} data-name={a.displayNumber} data-zusatz={a.title}>
          {`${a.displayNumber} · ${a.title}`}
        </option>
      ))}
    </Auswahlmenue>
  );
}
